import { AccountInfo, Configuration, PublicClientApplication } from "@azure/msal-browser";
import { FetchArgs } from "@reduxjs/toolkit/dist/query/fetchBaseQuery";
import { BaseQueryApi } from "@reduxjs/toolkit/query";
import { getATK, graphRequest, loginRequest, msalConfig, powerBiRequest, oneClickRequest } from "../authConfig";
import { updateAuthResult, updateGraphToken, updateOneClickCxReportToken, updatePbiToken } from "../slices/Auth.slice";
import { RootState } from "../store/store";
import { baseQuery } from "./baseQuery";

export const baseQueryWithReauth = async (args: string | FetchArgs, store: BaseQueryApi, extraOptions: any) => {
  const msalInstance = new PublicClientApplication(msalConfig as Configuration);
  await msalInstance.initialize();
  // FIRST FETCH
  let result = await baseQuery(args, store, extraOptions);

  // 401 error (Token Expiry)
  if (result.error && result.error.status === 401) {
    const getPromises = (account: AccountInfo) => [
      getATK(msalInstance, graphRequest.scopes, account),
      getATK(msalInstance, powerBiRequest.scopes, account),
      getATK(msalInstance, oneClickRequest.scopes, account)
    ];
    const userEmail = (store.getState() as RootState).auth.user?.email || "";
    const activeAccount = msalInstance.getAllAccounts().find(account => account.username === userEmail) || null;

    if (!activeAccount) {
      msalInstance.loginRedirect(loginRequest);
      return result;
    }

    msalInstance
      .acquireTokenSilent({
        ...loginRequest,
        account: activeAccount
      })
      .then(async (value) => {
        sessionStorage.setItem("token", value.accessToken);
        sessionStorage.setItem("expiresOn", value.expiresOn?.toString() ?? "");
        store.dispatch(updateAuthResult(value));
        // SECOND FETCH (Retry the Original Request)
        result = await baseQuery(args, store, extraOptions);
        if (result.error && result.error.status === 401) {
          console.error("Failed to refresh token:", result.error);
          return Promise.allSettled([]);
        }
        return Promise.allSettled(getPromises(activeAccount));
      })
      .then((values) => {
        for (let idx = 0; idx < values.length; idx++) {
          const [value] = values
            .filter((value): value is PromiseFulfilledResult<any> => value.status === "fulfilled")
            .map(({ value }) => value);

          if (value) {
            const token = value.accessToken;
            switch (idx) {
              case 0:
                store.dispatch(updateGraphToken(token));
                break;
              case 1:
                store.dispatch(updatePbiToken(token));
                break;
              case 2:
                store.dispatch(updateOneClickCxReportToken(token));
                break;
              default:
                break;
            }
          }
        }
      })
      .catch(console.error);
  }

  return result;
};
